import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';

import {
  BalanceSheetAccount,
  BalanceSheetEntitySettings,
  BalanceSheetEntitySettingsKeys,
} from './grid-config-context';
import {
  BalanceSheetEntitySettingsPayload,
  NewBudgetAttribute,
  PendingGridChanges,
  RemovedBudgetAttribute,
  RenamedBudgetAttribute,
} from '../../../new-manage-balance-sheets-accounts-component-modal.component';
import { HeaderState } from '../../../services/balance-sheet-header.context';

export function buildBalanceSheetRows(
  parsedSettings: BalanceSheetEntitySettings,
  jeApManualClearing: boolean
): BalanceSheetAccount[] {
  let keys = [
    BalanceSheetEntitySettingsKeys.ACCRUED_LIABILITIES,
    BalanceSheetEntitySettingsKeys.PREPAID_EXPENSES,
    BalanceSheetEntitySettingsKeys.LONG_TERM_PREPAID,
    BalanceSheetEntitySettingsKeys.ACCOUNTS_PAYABLE,
  ];

  if (!jeApManualClearing) {
    keys = keys.filter((key) => key !== 'ACCOUNTS_PAYABLE');
  }

  const rows: BalanceSheetAccount[] = [];

  for (const key of keys) {
    const row = parsedSettings[key];

    if (row) {
      const attributes = Object.entries(row.attributes || {}).reduce(
        (acc, [key, value]) => {
          acc[getEncodedAttributeName(key)] = value;

          return acc;
        },
        {} as Record<string, string>
      );

      rows.push({
        attributes,
        program: row.PROGRAM || '',
        project: row.PROJECT || '',
        type: BalanceSheetEntitySettingsKeys[key],
      });
    } else {
      rows.push({
        attributes: {},
        program: '',
        project: '',
        type: BalanceSheetEntitySettingsKeys[key],
      });
    }
  }

  return rows;
}

export function getPendingBalanceSheetChanges({
  currentColumns,
  currentRowData,
  headerStateSnapshot,
}: {
  currentColumns: string[];
  currentRowData: BalanceSheetAccount[];
  headerStateSnapshot: HeaderState;
}): PendingGridChanges {
  const { newBudgetAttributes, removedBudgetAttributes, renamedBudgetAttributes } =
    extractAttributeDefinitionChanges(headerStateSnapshot, currentColumns);

  const entitySettingsToSave = prepareEntitySettingsPayload({
    currentColumns,
    currentRowData,
    newBudgetAttributes,
    renamedBudgetAttributes,
  });

  return {
    entitySettingsToSave,
    newBudgetAttributes,
    removedBudgetAttributes,
    renamedBudgetAttributes,
  };
}

function extractAttributeDefinitionChanges(
  headerStateSnapshot: HeaderState,
  currentColumns: string[]
): {
  newBudgetAttributes: NewBudgetAttribute[];
  removedBudgetAttributes: RemovedBudgetAttribute[];
  renamedBudgetAttributes: RenamedBudgetAttribute[];
} {
  const newBudgetAttributes: NewBudgetAttribute[] = [];
  const removedBudgetAttributes: RemovedBudgetAttribute[] = [];
  const renamedBudgetAttributes: RenamedBudgetAttribute[] = [];

  currentColumns.forEach((col) => {
    const attrState = headerStateSnapshot[col];

    if (!attrState) {
      return;
    }

    if (attrState.added) {
      newBudgetAttributes.push({ colID: col, name: attrState.renamedValue || col });
    } else if (attrState.renamed) {
      renamedBudgetAttributes.push({
        name: col,
        new_name: attrState.renamedValue,
      });
    }
  });

  for (const [key, attrState] of Object.entries(headerStateSnapshot)) {
    if (currentColumns.includes(key)) {
      continue;
    }

    if (attrState.deleted) {
      removedBudgetAttributes.push({ name: key });
    }
  }

  return {
    newBudgetAttributes,
    removedBudgetAttributes,
    renamedBudgetAttributes,
  };
}

function prepareEntitySettingsPayload({
  currentColumns,
  currentRowData,
  newBudgetAttributes,
  renamedBudgetAttributes,
}: {
  currentColumns: string[];
  currentRowData: BalanceSheetAccount[];
  newBudgetAttributes: NewBudgetAttribute[];
  renamedBudgetAttributes: RenamedBudgetAttribute[];
}): BalanceSheetEntitySettingsPayload {
  const entitySettingsToSave: BalanceSheetEntitySettingsPayload = {};

  currentRowData.forEach((row) => {
    const unencodedAttributes: Record<string, string> = {};

    // Iterate over the attributes that are currently active and not deleted,
    // based on the latest header state.
    for (const colId of currentColumns) {
      const valueFromRow = row.attributes[getEncodedAttributeName(colId)];

      // Only include the attribute in the payload if it has a value in the row data.
      // If an attribute was added but never had data entered, it might not be in row.attributes.
      if (valueFromRow !== undefined) {
        let colIdToUse = colId;

        const newBudgetAttribute = newBudgetAttributes.find((attr) => attr.colID === colId);
        const renamedBudgetAttribute = renamedBudgetAttributes.find((attr) => attr.name === colId);

        if (newBudgetAttribute) {
          colIdToUse = newBudgetAttribute.name;
        } else if (renamedBudgetAttribute) {
          colIdToUse = renamedBudgetAttribute.new_name;
        }

        unencodedAttributes[colIdToUse] = String(valueFromRow ?? '');
      }
    }

    entitySettingsToSave[row.type] = {
      attributes: unencodedAttributes,
      PROGRAM: row.program,
      PROJECT: row.project,
    };
  });

  return entitySettingsToSave;
}
